import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheck, Mail, LogIn, LogOut, AlertTriangle, RefreshCw,
  Search, Filter, ChevronDown, ChevronUp, Loader2, AlertCircle,
  CheckCircle2, XCircle, Clock, User, Building2, Ticket,
  Eye, FileText, BarChart3, Download, Plug, Zap, ZapOff, Activity,
  HardDrive, Monitor,
} from 'lucide-react';
import { Button } from '../ui/button';

interface AuditLogsTabProps {
  token: string;
}

interface AuditRow {
  id: string;
  event_type: string;
  actor_id: string | null;
  actor_email: string | null;
  actor_role: string | null;
  client_id: string | null;
  client_name: string | null;
  ip_address: string | null;
  description: string;
  metadata: Record<string, any>;
  status: string;
  created_at: string;
}

interface EmailRow {
  id: string;
  event_type: string;
  recipient: string;
  subject: string;
  status: string;
  error_message: string | null;
  client_id: string | null;
  ticket_id: string | null;
  metadata: Record<string, any>;
  created_at: string;
}

// ── Styling helpers ──────────────────────────────────────────────────────────

const EVENT_META: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  plc_connected:        { label: 'PLC Connected',        icon: <Plug className="h-3.5 w-3.5" />,        color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30' },
  plc_disconnected:     { label: 'PLC Disconnected',     icon: <ZapOff className="h-3.5 w-3.5" />,      color: 'bg-red-500/10 text-red-500 border-red-500/30' },
  energy_meter_connected: { label: 'Meter Connected',     icon: <Zap className="h-3.5 w-3.5" />,         color: 'bg-blue-500/10 text-blue-500 border-blue-500/30' },
  energy_meter_disconnected: { label: 'Meter Disconnected', icon: <ZapOff className="h-3.5 w-3.5" />,      color: 'bg-red-500/10 text-red-500 border-red-500/30' },
  hardware_event:       { label: 'Hardware Alert',       icon: <Activity className="h-3.5 w-3.5" />,    color: 'bg-amber-500/10 text-amber-500 border-amber-500/30' },
  login_success:        { label: 'Login Success',        icon: <LogIn className="h-3.5 w-3.5" />,       color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30' },
  login_failed:         { label: 'Login Failed',         icon: <XCircle className="h-3.5 w-3.5" />,     color: 'bg-red-500/10 text-red-500 border-red-500/30' },
  license_expired_login:{ label: 'License Expired',      icon: <AlertTriangle className="h-3.5 w-3.5" />,color: 'bg-amber-500/10 text-amber-500 border-amber-500/30' },
  ticket_created:       { label: 'Ticket Created',       icon: <Ticket className="h-3.5 w-3.5" />,       color: 'bg-blue-500/10 text-blue-500 border-blue-500/30' },
  ticket_updated:       { label: 'Ticket Updated',       icon: <FileText className="h-3.5 w-3.5" />,     color: 'bg-purple-500/10 text-purple-500 border-purple-500/30' },
  ticket_comment_added: { label: 'Ticket Comment',       icon: <FileText className="h-3.5 w-3.5" />,     color: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/30' },
  ticket_deleted:       { label: 'Ticket Deleted',       icon: <XCircle className="h-3.5 w-3.5" />,      color: 'bg-red-500/10 text-red-500 border-red-500/30' },
  pdf_download:         { label: 'PDF Download',        icon: <Download className="h-3.5 w-3.5" />,     color: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/30' },
  csv_download:         { label: 'CSV Download',        icon: <Download className="h-3.5 w-3.5" />,     color: 'bg-sky-500/10 text-sky-500 border-sky-500/30' },
  download_energy_data: { label: 'Energy Download',     icon: <Download className="h-3.5 w-3.5" />,     color: 'bg-green-500/10 text-green-500 border-green-500/30' },
  download_manual_logs: { label: 'Manual Download',     icon: <Download className="h-3.5 w-3.5" />,     color: 'bg-violet-500/10 text-violet-500 border-violet-500/30' },
  download_scrap_data:  { label: 'Scrap Download',      icon: <Download className="h-3.5 w-3.5" />,     color: 'bg-orange-500/10 text-orange-500 border-orange-500/30' },
  download_historical_data: { label: 'Historical Download', icon: <Download className="h-3.5 w-3.5" />,  color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30' },
  manual_log_created:   { label: 'Manual Log Created',  icon: <FileText className="h-3.5 w-3.5" />,    color: 'bg-green-500/10 text-green-500 border-green-500/30' },
  manual_log_deleted:   { label: 'Manual Log Deleted',  icon: <XCircle className="h-3.5 w-3.5" />,     color: 'bg-red-500/10 text-red-500 border-red-500/30' },
  scrap_entry_created:  { label: 'Scrap Entry Created', icon: <FileText className="h-3.5 w-3.5" />,    color: 'bg-orange-500/10 text-orange-500 border-orange-500/30' },
  scrap_entry_deleted:  { label: 'Scrap Entry Deleted', icon: <XCircle className="h-3.5 w-3.5" />,     color: 'bg-red-500/10 text-red-500 border-red-500/30' },
  logout:               { label: 'Logout',              icon: <LogOut className="h-3.5 w-3.5" />,       color: 'bg-slate-500/10 text-slate-500 border-slate-500/30' },
  password_changed:     { label: 'Password Changed',    icon: <ShieldCheck className="h-3.5 w-3.5" />,   color: 'bg-amber-500/10 text-amber-500 border-amber-500/30' },
  general:              { label: 'General',              icon: <ShieldCheck className="h-3.5 w-3.5" />,  color: 'bg-muted text-muted-foreground border-border' },
};

const EMAIL_EVENT_META: Record<string, { label: string; color: string }> = {
  license_expired:  { label: 'License Expired',     color: 'bg-red-500/10 text-red-500 border-red-500/30' },
  license_warning:  { label: 'License Warning',     color: 'bg-amber-500/10 text-amber-500 border-amber-500/30' },
  license_renewed:  { label: 'License Renewed',     color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30' },
  ticket_created:   { label: 'Ticket Created',      color: 'bg-blue-500/10 text-blue-500 border-blue-500/30' },
  ticket_status:    { label: 'Ticket Status',       color: 'bg-purple-500/10 text-purple-500 border-purple-500/30' },
  ticket_comment:   { label: 'Ticket Comment',      color: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/30' },
  ticket_comment_admin: { label: 'Admin Comment',    color: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/30' },
  general:          { label: 'General',             color: 'bg-muted text-muted-foreground border-border' },
};

const STATUS_STYLE: Record<string, string> = {
  success: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  failure: 'bg-red-500/10 text-red-500 border-red-500/30',
  warning: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  sent:    'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  failed:  'bg-red-500/10 text-red-500 border-red-500/30',
};

const fmtDate = (d: string) =>
  new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });

const timeAgo = (d: string) => {
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'Just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const EventBadge = ({ type }: { type: string }) => {
  const m = EVENT_META[type] || EVENT_META.general;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${m.color}`}>
      {m.icon}{m.label}
    </span>
  );
};

const StatusBadge = ({ status }: { status: string }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border capitalize ${STATUS_STYLE[status] || 'bg-muted text-muted-foreground border-border'}`}>
    {status}
  </span>
);

// ── Main Component ────────────────────────────────────────────────────────────

export function AuditLogsTab({ token }: AuditLogsTabProps) {
  const [tab, setTab] = useState<'audit' | 'email'>('audit');
  const [auditLogs, setAuditLogs] = useState<AuditRow[]>([]);
  const [emailLogs, setEmailLogs] = useState<EmailRow[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [eventFilter, setEventFilter] = useState('all');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch('/api/admin/audit-logs', { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAuditLogs(data.logs || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEmailLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch('/api/admin/email-logs', { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEmailLogs(data.logs || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load email logs');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const res = await fetch('/api/clients', { headers });
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients || []);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    if (tab === 'audit') fetchAuditLogs();
    else fetchEmailLogs();
    setPage(1);
    setSearch('');
    setStatusFilter('all');
    setEventFilter('all');
    setSelectedClientId('all');
    setExpandedRow(null);
  }, [tab, fetchAuditLogs, fetchEmailLogs]);

  // ── Stats ────────────────────────────

  const auditStats = {
    total:   auditLogs.length,
    success: auditLogs.filter(l => l.status === 'success').length,
    failure: auditLogs.filter(l => l.status === 'failure').length,
    warning: auditLogs.filter(l => l.status === 'warning').length,
    logins:  auditLogs.filter(l => l.event_type.startsWith('login')).length,
  };

  const emailStats = {
    total:  emailLogs.length,
    sent:   emailLogs.filter(l => l.status === 'sent').length,
    failed: emailLogs.filter(l => l.status === 'failed').length,
  };

  // ── Filtering ───────────────────────

  const filteredAudit = auditLogs.filter(l => {
    const matchSearch = !search || [l.description, l.actor_email, l.client_name, l.ip_address, l.event_type]
      .some(f => f?.toLowerCase().includes(search.toLowerCase()));
    const matchStatus = statusFilter === 'all' || l.status === statusFilter;
    const matchEvent  = eventFilter  === 'all' || l.event_type === eventFilter;
    const matchClient = selectedClientId === 'all' || l.client_id === selectedClientId;
    return matchSearch && matchStatus && matchEvent && matchClient;
  });

  const filteredEmail = emailLogs.filter(l => {
    const matchSearch = !search || [l.subject, l.recipient, l.event_type, l.error_message]
      .some(f => f?.toLowerCase().includes(search.toLowerCase()));
    const matchStatus = statusFilter === 'all' || l.status === statusFilter;
    const matchClient = selectedClientId === 'all' || l.client_id === selectedClientId;
    return matchSearch && matchStatus && matchClient;
  });

  const pagedAudit = filteredAudit.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pagedEmail = filteredEmail.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil((tab === 'audit' ? filteredAudit : filteredEmail).length / PAGE_SIZE);

  const inputClass = "pl-9 pr-4 py-2 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm";

  // ── Unique event types for filter ──
  const uniqueEventTypes = [...new Set(auditLogs.map(l => l.event_type))];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-violet-500/10 border border-violet-500/20">
            <ShieldCheck className="h-5 w-5 text-violet-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Audit & Email Logs</h1>
            <p className="text-muted-foreground text-sm">Complete record of system events, logins, and emails</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={tab === 'audit' ? fetchAuditLogs : fetchEmailLogs}
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />Refresh
        </Button>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3">
          <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Tab Switcher */}
      <div className="flex gap-2 p-1 bg-muted/50 rounded-xl border border-border w-fit">
        {[
          { id: 'audit', label: 'Audit Log', icon: ShieldCheck },
          { id: 'email', label: 'Email Log', icon: Mail },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === t.id
                  ? 'bg-card shadow border border-border text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Stats */}
      {tab === 'audit' ? (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: 'Total Events', count: auditStats.total,   color: 'text-foreground' },
            { label: 'Success',      count: auditStats.success, color: 'text-emerald-500' },
            { label: 'Failures',     count: auditStats.failure, color: 'text-red-500' },
            { label: 'Warnings',     count: auditStats.warning, color: 'text-amber-500' },
            { label: 'Login Events', count: auditStats.logins,  color: 'text-blue-500' },
          ].map(s => (
            <div key={s.label} className="bg-card border border-border rounded-xl p-4 text-center">
              <p className={`text-2xl font-bold ${s.color}`}>{s.count}</p>
              <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { label: 'Total Emails', count: emailStats.total,  color: 'text-foreground' },
            { label: 'Sent',         count: emailStats.sent,   color: 'text-emerald-500' },
            { label: 'Failed',       count: emailStats.failed, color: 'text-red-500' },
          ].map(s => (
            <div key={s.label} className="bg-card border border-border rounded-xl p-4 text-center">
              <p className={`text-2xl font-bold ${s.color}`}>{s.count}</p>
              <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-0 sm:min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder={tab === 'audit' ? 'Search by description, email, IP...' : 'Search by subject, recipient...'}
            className={`w-full ${inputClass}`}
          />
        </div>
        <div className="relative w-full sm:w-auto sm:min-w-[200px]">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <select
            value={selectedClientId}
            onChange={e => { setSelectedClientId(e.target.value); setPage(1); }}
            className={`w-full appearance-none ${inputClass}`}
          >
            <option value="all">All Clients</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        </div>
        <div className="flex gap-2">
          {['all', 'success', 'failure', 'warning'].map(s => tab === 'audit' ? (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all capitalize ${
                statusFilter === s
                  ? 'bg-blue-500/10 border-blue-500/40 text-blue-500'
                  : 'border-border text-muted-foreground hover:border-muted-foreground bg-background'
              }`}
            >{s}</button>
          ) : null)}
          {tab === 'email' && ['all', 'sent', 'failed'].map(s => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all capitalize ${
                statusFilter === s
                  ? 'bg-blue-500/10 border-blue-500/40 text-blue-500'
                  : 'border-border text-muted-foreground hover:border-muted-foreground bg-background'
              }`}
            >{s}</button>
          ))}
          {tab === 'audit' && uniqueEventTypes.length > 0 && (
            <select
              value={eventFilter}
              onChange={e => { setEventFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg text-xs border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            >
              <option value="all">All Events</option>
              {uniqueEventTypes.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-16 text-center">
            <Loader2 className="mx-auto h-10 w-10 text-violet-500 animate-spin mb-4" />
            <p className="text-muted-foreground">Loading logs...</p>
          </div>
        ) : tab === 'audit' ? (
          // ── Audit Log Table ────────────────────────────────────
          pagedAudit.length === 0 ? (
            <div className="p-16 text-center">
              <ShieldCheck className="mx-auto h-12 w-12 text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground">No audit logs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border bg-muted/30">
                  <tr>
                    {['Event', 'Actor', 'Client', 'Description', 'Status', 'When', ''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {pagedAudit.map((row, i) => (
                    <React.Fragment key={row.id}>
                      <motion.tr
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.02 }}
                        className="hover:bg-accent/40 cursor-pointer transition-colors"
                        onClick={() => setExpandedRow(expandedRow === row.id ? null : row.id)}
                      >
                        <td className="px-4 py-3">
                          <EventBadge type={row.event_type} />
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-foreground">{row.actor_email || '—'}</p>
                          <p className="text-xs text-muted-foreground capitalize">{row.actor_role?.replace('_', ' ') || ''}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-foreground">{row.client_name || '—'}</p>
                          {row.ip_address && <p className="text-xs text-muted-foreground font-mono">{row.ip_address}</p>}
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          <p className="text-sm text-foreground truncate">{row.description}</p>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={row.status} />
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                          <p>{timeAgo(row.created_at)}</p>
                          <p className="text-xs">{new Date(row.created_at).toLocaleDateString()}</p>
                        </td>
                        <td className="px-4 py-3">
                          {expandedRow === row.id
                            ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                            : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                        </td>
                      </motion.tr>
                      <AnimatePresence>
                        {expandedRow === row.id && (
                          <tr>
                            <td colSpan={7} className="px-0 py-0">
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="bg-muted/20 border-t border-border px-4 sm:px-6 py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm"
                              >
                                <div><p className="text-xs text-muted-foreground mb-1">Event ID</p><p className="font-mono text-xs text-foreground">{row.id}</p></div>
                                <div><p className="text-xs text-muted-foreground mb-1">Timestamp</p><p className="text-foreground">{fmtDate(row.created_at)}</p></div>
                                <div><p className="text-xs text-muted-foreground mb-1">Actor ID</p><p className="font-mono text-xs text-foreground">{row.actor_id || '—'}</p></div>
                                <div><p className="text-xs text-muted-foreground mb-1">Client ID</p><p className="font-mono text-xs text-foreground">{row.client_id || '—'}</p></div>
                                {Object.keys(row.metadata || {}).length > 0 && (
                                  <div className="col-span-4">
                                    <p className="text-xs text-muted-foreground mb-1">Metadata</p>
                                    <pre className="text-xs bg-background border border-border rounded-lg p-3 overflow-x-auto text-foreground">
                                      {JSON.stringify(row.metadata, null, 2)}
                                    </pre>
                                  </div>
                                )}
                              </motion.div>
                            </td>
                          </tr>
                        )}
                      </AnimatePresence>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          // ── Email Log Table ────────────────────────────────────
          pagedEmail.length === 0 ? (
            <div className="p-16 text-center">
              <Mail className="mx-auto h-12 w-12 text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground">No email logs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border bg-muted/30">
                  <tr>
                    {['Type', 'Recipient', 'Subject', 'Status', 'Client', 'When'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {pagedEmail.map((row, i) => {
                    const meta = EMAIL_EVENT_META[row.event_type] || EMAIL_EVENT_META.general;
                    return (
                      <motion.tr
                        key={row.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.02 }}
                        className="hover:bg-accent/40 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${meta.color}`}>
                            <Mail className="h-3 w-3" />{meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-foreground">{row.recipient}</p>
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          <p className="text-sm text-foreground truncate">{row.subject}</p>
                          {row.error_message && (
                            <p className="text-xs text-red-500 mt-0.5 truncate">⚠ {row.error_message}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={row.status} />
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-xs font-mono text-muted-foreground">{row.client_id ? row.client_id.slice(0, 20) + '…' : '—'}</p>
                          {row.ticket_id && <p className="text-xs text-blue-500">{row.ticket_id.slice(0, 20)}…</p>}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                          <p>{timeAgo(row.created_at)}</p>
                          <p className="text-xs">{new Date(row.created_at).toLocaleDateString()}</p>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Page {page} of {totalPages} · {(tab === 'audit' ? filteredAudit : filteredEmail).length} results
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</Button>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next →</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
